import { ReactElement, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslate } from '@client/i18n/hooks';
import { useRouterPush } from '@client/routes/hooks';
import { Location } from 'history';
import { NavigationPrompt } from './NavigationPrompt';
import { Props } from './NavigationPromptContainer.types';

export const NavigationPromptContainer = ({ shouldConfirmNavigation, onNavigationConfirm }: Props): ReactElement => {
  const location = useLocation();
  const push = useRouterPush();
  const [nextLocation, setNextLocation] = useState<Location | null>(null);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isNavigationConfirmed, setIsNavigationConfirmed] = useState(false);
  const t = useTranslate();

  const handleNavigationBlock = (nextLocation: Location): boolean => {
    if (
      !isNavigationConfirmed
      && shouldConfirmNavigation
      && location.pathname !== nextLocation.pathname
    ) {
      setNextLocation(nextLocation);
      setIsModalOpened(true);
      return false;
    }

    return true;
  };

  const handleNavigationConfirm = (): void => {
    onNavigationConfirm?.();
    setIsModalOpened(false);
    setIsNavigationConfirmed(true);
  };

  const handleNavigationCancel = (): void => {
    setIsModalOpened(false);
  };

  useEffect((): void => {
    if (nextLocation && isNavigationConfirmed) {
      push(nextLocation.pathname + nextLocation.search);
    }
  }, [isNavigationConfirmed, !!nextLocation]);

  const handleBeforeUnload = (event: BeforeUnloadEvent): void | string => {
    event.preventDefault();

    if (!isNavigationConfirmed && shouldConfirmNavigation && !isModalOpened) {
      return event.returnValue = t('Leave the page? All data entered in the form will be lost.');
    }
  };

  useEffect(
    () => {
      if (shouldConfirmNavigation) {
        window?.addEventListener('beforeunload', handleBeforeUnload);
      } else {
        window?.removeEventListener('beforeunload', handleBeforeUnload);
      }

      return () => window?.removeEventListener('beforeunload', handleBeforeUnload);
    },
    [shouldConfirmNavigation],
  );

  return (
    <NavigationPrompt
      shouldConfirmNavigation={shouldConfirmNavigation}
      isModalOpened={isModalOpened}
      onNavigationCancel={handleNavigationCancel}
      onNavigationConfirm={handleNavigationConfirm}
      onNavigationBlock={handleNavigationBlock}
    />
  );
};
